import { defineStore } from 'pinia'
import { useAuthStore } from './auth'
import type {
  DynamicActionsMenu,
  DynamicComponent,
  SnackBanner,
  SnackTypes,
  TableAction,
  TableColumn,
  TablesEnum,
  Toast,
  UsersFilters,
  UserPermissionsFilters,
  Filters,
} from '@/types'
import type {
  PropertiesExclusiveFilters,
  PropertiesFilters,
  Property,
  PropertyDynamicExclusivesUnits,
  PropertyExclusiveFilters,
} from '~/types/properties'
import type { DevelopersFilters } from '~/types/developer'
import type { TOURS_IDS } from '~/constants'

type BaseTableFilters = {
  users: UsersFilters | undefined
  properties: PropertiesFilters | undefined
  developers: DevelopersFilters | undefined
  amenities: Filters | undefined
  propertiesExclusivesUnits: PropertiesExclusiveFilters | undefined
  newsFeed: Filters | undefined
  marketingMaterials: Filters | undefined
  usefulMaterials: Filters | undefined
  userManuals: Filters | undefined
  userPermissions: UserPermissionsFilters | undefined
}

type DynamicTableFilters = {
  [key in PropertyDynamicExclusivesUnits]?: PropertyExclusiveFilters
}

type UiState = {
  tableFilters: BaseTableFilters & DynamicTableFilters
  snackBanners: SnackBanner[]
  dynamicPopups: DynamicComponent[]
  dynamicSidePanel: DynamicComponent
  dynamicMobilePanel: DynamicComponent
  dynamicActionBar: DynamicComponent
  dynamicActionsMenu: DynamicActionsMenu
  dynamicTour: DynamicComponent
  tableItemsPerPage: number
  toasts: Toast[]
  columns: {
    [key: string]: TableColumn[]
  }
}

const initDynamicComponent = () => ({
  value: false,
  component: undefined,
  data: {},
  events: {},
})

const initActionsMenu = () => ({
  value: false,
  actions: [],
})

const initialSnackBanner = [] as UiState['snackBanners']

export const useUiStore = defineStore('ui', {
  state: (): UiState => ({
    tableFilters: {
      users: undefined,
      properties: undefined,
      developers: undefined,
      amenities: undefined,
      propertiesExclusivesUnits: undefined,
      newsFeed: undefined,
      marketingMaterials: undefined,
      usefulMaterials: undefined,
      userManuals: undefined,
      userPermissions: undefined,
    },
    snackBanners: [...initialSnackBanner],
    dynamicPopups: [],
    dynamicSidePanel: initDynamicComponent(),
    dynamicMobilePanel: initDynamicComponent(),
    dynamicActionBar: initDynamicComponent(),
    dynamicActionsMenu: initActionsMenu(),
    dynamicTour: initDynamicComponent(),
    tableItemsPerPage: 20,
    toasts: [],
    columns: {},
  }),
  actions: {
    showSnackBanner(
      text: string = '',
      type: SnackTypes = 'success',
      action?: (...args: any[]) => any,
      actionButtonText?: string,
      secondaryAction?: (...args: any[]) => any,
      secondaryActionButtonText?: string
    ) {
      this.snackBanners.push({
        id: Date.now(),
        value: true,
        type,
        text: text || (type === 'success' ? 'Success' : 'Something went wrong'),
        action,
        actionButtonText,
        secondaryAction,
        secondaryActionButtonText,
      })
    },
    showPopup(component: Component, data: Record<string, any> = {}, events: Record<string, any> = {}) {
      const id = crypto.randomUUID()
      this.dynamicPopups.push({
        id,
        value: true,
        component: markRaw(component),
        data,
        events,
      })
      return id
    },
    cleanPopup(id: DynamicComponent['id']) {
      this.dynamicPopups = this.dynamicPopups.filter((popup) => popup.id !== id)
    },
    cleanAllPopups() {
      this.dynamicPopups = []
    },
    showSidePanel(component: Component, data: Record<string, any> = {}, events: Record<string, any> = {}) {
      this.dynamicSidePanel = {
        value: true,
        component: markRaw(component),
        data,
        events,
      }
    },
    cleanSidePanel() {
      this.dynamicSidePanel = initDynamicComponent()
    },
    showMobilePanel(component: Component, data: Record<string, any> = {}, events: Record<string, any> = {}) {
      this.dynamicMobilePanel = {
        value: true,
        component: markRaw(component),
        data,
        events,
      }
    },
    cleanMobilePanel() {
      this.dynamicMobilePanel = initDynamicComponent()
    },
    showActionBar(component: Component, data: Record<string, any> = {}, events: Record<string, any> = {}) {
      this.dynamicActionBar = {
        value: true,
        component: markRaw(component),
        data,
        events,
      }
    },
    cleanActionBar() {
      this.dynamicActionBar = initDynamicComponent()
    },
    resetSnackBanner(id: UiState['snackBanners'][number]['id']) {
      this.snackBanners = this.snackBanners.filter((item) => item.id !== id)
    },
    setTableFilters(table: TablesEnum, filters: any) {
      this.tableFilters[table as keyof typeof this.tableFilters] = filters
    },
    showDynamicActionsMenu(name: string, actions: TableAction[], x: number, y: number) {
      if (this.dynamicActionsMenu?.value) {
        this.cleanDynamicActionsMenu()
        setTimeout(() => {
          this.dynamicActionsMenu = {
            value: true,
            actions,
            name,
            x,
            y,
          }
        })
      } else {
        this.dynamicActionsMenu = {
          value: true,
          actions,
          name,
          x,
          y,
        }
      }
    },
    cleanDynamicActionsMenu() {
      this.dynamicActionsMenu = initActionsMenu()
    },
    setTableItemsPerPage(value: number) {
      this.tableItemsPerPage = value
    },
    showToast(type: string, props: Record<string, any>, options?: { autoClose?: boolean; duration?: number }) {
      const id = Date.now()
      this.toasts.unshift({ id, type, props })

      const { autoClose = true, duration = 5000 } = options || {}

      if (autoClose) {
        setTimeout(() => {
          this.removeToast(id)
        }, duration)
      }

      return id
    },
    updateToastProps(id: number, newProps: Record<string, any>) {
      this.toasts = this.toasts.map((toast: Toast) =>
        toast.id === id ? { ...toast, props: { ...toast.props, ...newProps } } : toast
      )
    },
    removeToast(id: number) {
      this.toasts = this.toasts.filter((toast: Toast) => toast.id !== id)
    },
    setColumns(id: string | undefined, columns: TableColumn[]) {
      if (!id) return

      this.columns[id] = columns
    },
    showTour({
      component,
      data = {},
      events = {},
      id,
      checkCompleted = true,
    }: {
      component: Component
      data?: Record<string, any>
      events?: Record<string, any>
      id: TOURS_IDS
      checkCompleted?: boolean
    }) {
      const { getUser } = useAuthStore()
      if (getUser.tours?.includes(id) && checkCompleted) {
        // Tour has already been completed, no need to mount component
        return
      }

      if (this.dynamicTour.value) {
        // Tour is already mounted, no need to mount component
        return
      }

      this.dynamicTour = {
        value: true,
        component: markRaw(component),
        data,
        events,
      }
    },
    cleanTour() {
      this.dynamicTour = initDynamicComponent()
    },
  },
  getters: {
    getSnackBanners: (state) => state.snackBanners,
    getDynamicPopups: (state) => state.dynamicPopups,
    getPopupData: (state) => (id: DynamicComponent['id']) => state.dynamicPopups.find((popup) => popup.id === id)?.data,
    getDynamicSidePanel: (state) => state.dynamicSidePanel,
    getDynamicMobilePanel: (state) => state.dynamicMobilePanel,
    getDynamicActionBar: (state) => state.dynamicActionBar,
    getDynamicActionsMenu: (state) => state.dynamicActionsMenu,
    getDynamicTour: (state) => state.dynamicTour,
    getTableItemsPerPage: (state) => state.tableItemsPerPage,
    getToasts: (state) => state.toasts,
    getUsersFilters: (state) => state.tableFilters.users,
    getPropertiesFilters: (state) => state.tableFilters.properties,
    getDevelopersFilters: (state) => state.tableFilters.developers,
    getPropertiesExclusiveFilters: (state) => state.tableFilters.propertiesExclusivesUnits,
    getAmenitiesFilters: (state) => state.tableFilters.amenities,
    getPropertyExclusivesUnits: (state) => {
      return (id: Property['id']) => state.tableFilters[`propertyExclusivesUnits-${id}`]
    },
    getColumns: (state) => {
      return (id?: string) => (id && state.columns[id] ? state.columns[id] : [])
    },
    getFilters(state) {
      return (table: TablesEnum) => {
        return state.tableFilters[table as keyof typeof state.tableFilters]
      }
    },
    useGetNewsPostFilters: (state) => state.tableFilters.newsFeed,
    useGetMarketingMaterialsFilters: (state) => state.tableFilters.marketingMaterials,
    useGetUsefulMaterialsFilters: (state) => state.tableFilters.usefulMaterials,
    useGetUserManualsFilters: (state) => state.tableFilters.userManuals,
    getUserPermissionsTableFilters: (state) => state.tableFilters.userPermissions,
  },
  persist: {
    storage: localStorage,
    pick: ['tableFilters', 'tableItemsPerPage', 'columns'],
  },
})

export type UiStore = ReturnType<typeof useUiStore>
